import { useMemo } from 'react';
import ConfirmDeleteDialog from '@/components/ui/ConfirmDeleteDialog';
import { useSelector } from 'react-redux';
import { Add16Regular, Delete16Regular } from '@fluentui/react-icons';
import { Button } from '@/components/ui/Button';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type ColumnDef,
} from '@tanstack/react-table';
import { cn } from '../../../../utils/cn';
import { Tooltip } from '@/components/ui/Tooltip';
import type {
  AppearanceComponentRef,
  TypemarkAppearance,
} from '@/features/project/appearances/types';

// ─── Types ───────────────────────────────────────────────────────────────────

type Solution = {
  title: string;
  classified_solution: string;
  appearance?: TypemarkAppearance | null;
  diff_appearance_flag?: boolean;
};

type ComponentRow = {
  id: string;
  title: string;
  solution: Solution | null;
  color: TypemarkAppearance | null;
  rowSpan?: number;
  diffRow?: boolean;
};

type TypeMarkComponentTableProps = {
  data: ComponentRow[];
  onDeleteSolution: (classifiedSolution: string, componentId: string) => void;
  onEditColor?: (componentId: string, classifiedSolutionId: string) => void;
  onDeleteColor?: (classifiedSolution: string, componentId: string, appearanceId: string) => void;
  onDeleteDiffColor?: (componentId: string, classifiedSolution: string) => void;
  onAdd?: (componentId: string) => void;
  onDelete?: () => void;
  onDeleteNonCommonSolutions?: (id: string) => void;
  onDeleteNonCommonColors?: () => void;
  isTemplate?: boolean;
  columns?: any[];
};

// ─── Component ───────────────────────────────────────────────────────────────

function AppearanceCell({ appearance }: { appearance: TypemarkAppearance }) {
  const parts = [appearance.pattern, appearance.finish].filter(
    (part): part is AppearanceComponentRef => !!part
  );
  const label = (
    <span
      className={cn(
        'inline-flex items-center gap-2 min-w-0',
        !appearance.is_active && 'opacity-60'
      )}
    >
      <span
        className="h-4 w-6 shrink-0 rounded border border-neutral-200"
        style={{ backgroundColor: appearance.hex || '#e4e5e7' }}
      />
      <span className={cn('text-neutral-700', !appearance.is_active && 'line-through')}>
        {appearance.title}
      </span>
      {appearance.custom_title && (
        <span className="font-mono text-[10px] text-neutral-500">{appearance.sys_title}</span>
      )}
      {!appearance.custom_title &&
        parts.map((part) => (
          <span key={part.code} className="text-[11px] text-neutral-500">
            {part.title}
          </span>
        ))}
    </span>
  );
  const tooltip = [
    !appearance.is_active && 'Neaktivní vzhled',
    ...parts.map((part) => `${part.code} ${part.title}`),
  ]
    .filter(Boolean)
    .join(' · ');
  return <Tooltip content={tooltip}>{label}</Tooltip>;
}

const columnHelper = createColumnHelper<ComponentRow>();

function TypeMarkComponentTable({
  data,
  onDeleteSolution,
  onEditColor,
  onDeleteColor,
  onDeleteDiffColor,
  onAdd,
  onDeleteNonCommonSolutions,
  isTemplate,
}: TypeMarkComponentTableProps) {
  const permission = useSelector((state: any) => state.auth.pagePermission);

  const columns = useMemo<ColumnDef<ComponentRow, any>[]>(
    () => [
      columnHelper.accessor('title', {
        header: 'Název',
        cell: ({ row }) => (
          <span className="font-medium text-neutral-900">{row.original.title}</span>
        ),
      }),
      columnHelper.accessor('solution', {
        header: 'Řešení',
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex items-center justify-between gap-2">
              <span className="text-neutral-700">{item.solution?.title}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor('color', {
        header: 'Vzhled',
        cell: ({ row }) => {
          const item = row.original;
          return (
            <span className="flex items-center gap-2 justify-between">
              {item.solution?.diff_appearance_flag ? (
                <span className="italic text-neutral-500">Jiné</span>
              ) : item.color ? (
                <AppearanceCell appearance={item.color} />
              ) : null}

              <div className="flex gap-1">
                {(item.solution?.appearance || item.solution?.diff_appearance_flag) &&
                  permission === 2 && (
                    <ConfirmDeleteDialog
                      onConfirm={() => {
                        if (item.solution?.diff_appearance_flag) {
                          onDeleteDiffColor?.(item.id, item.solution.classified_solution);
                        } else if (item.solution?.appearance) {
                          onDeleteColor?.(
                            item.solution.classified_solution,
                            item.id,
                            item.solution.appearance.id
                          );
                        }
                      }}
                    >
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-neutral-300 hover:text-red-600"
                        data-testid={`typemark-component-delete-color-btn-${item.id}`}
                      >
                        <Delete16Regular />
                      </Button>
                    </ConfirmDeleteDialog>
                  )}
              </div>
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'solutionActions',
        header: '',
        meta: { isAction: true },
        cell: ({ row }) => {
          const item = row.original;
          if (item.diffRow) return null;
          return (
            <div className="flex gap-1 justify-center">
              {item.solution && permission === 2 && !isTemplate && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-neutral-300 hover:text-neutral-700"
                  data-testid={`typemark-component-add-color-btn-${item.id}`}
                  onClick={() => onEditColor?.(item.id, item.solution!.classified_solution)}
                >
                  <Add16Regular />
                </Button>
              )}
              {item.solution && permission === 2 && (
                <ConfirmDeleteDialog
                  onConfirm={() => onDeleteSolution(item.solution!.classified_solution, item.id)}
                >
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-neutral-300 hover:text-red-600"
                    data-testid={`typemark-component-delete-solution-btn-${item.id}`}
                  >
                    <Delete16Regular />
                  </Button>
                </ConfirmDeleteDialog>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'componentActions',
        header: '',
        meta: { isAction: true },
        cell: ({ row }) => {
          const item = row.original;
          if (item.diffRow) return null;
          if (item.rowSpan === 0) return null;
          return (
            <>
              {onAdd && permission === 2 && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-neutral-300 hover:text-neutral-700"
                  data-testid={`typemark-component-add-solution-btn-${item.id}`}
                  onClick={() => onAdd(item.id)}
                >
                  <Add16Regular />
                </Button>
              )}
            </>
          );
        },
      }),
    ],
    [permission, isTemplate, onEditColor, onDeleteColor, onDeleteDiffColor, onDeleteSolution, onAdd]
  );

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => `${row.id}-${index}`,
  });

  return (
    <div className="border border-neutral-150 rounded overflow-hidden bg-white">
      <table className="w-full text-sm border-collapse">
        <thead className="border-b border-neutral-200 bg-neutral-25">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, hIdx) => {
                const isAction = (header.column.columnDef.meta as any)?.isAction;
                const isLast = hIdx === headerGroup.headers.length - 1;
                return (
                  <th
                    key={header.id}
                    className={cn(
                      'px-3 py-2 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-500',
                      !isLast && 'border-r border-neutral-150',
                      isAction && 'w-[44px]'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row, rowIndex) => {
              const item = row.original;
              const allRows = table.getRowModel().rows;
              const nextItem = allRows[rowIndex + 1]?.original;
              const isGroupStart = item.rowSpan && item.rowSpan > 0;
              const isLastRow = !nextItem;
              const isLastInGroup =
                isLastRow || (nextItem.rowSpan && nextItem.rowSpan > 0) || nextItem.diffRow;
              const isInnerRow = item.rowSpan === 0;

              if (item.diffRow) {
                return (
                  <tr key={row.id} className="border-b border-neutral-200 bg-amber-50/40">
                    <td
                      colSpan={3}
                      className="px-3 py-2 text-sm border-r border-neutral-150 italic text-neutral-500"
                    >
                      <div className="flex justify-between items-center">
                        <span>Různé řešení</span>
                        <ConfirmDeleteDialog
                          title="Opravdu chcete položky odstranit?"
                          onConfirm={() => onDeleteNonCommonSolutions?.(item.id)}
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-neutral-300 hover:text-red-600"
                            data-testid={`typemark-component-delete-non-common-btn-${item.id}`}
                          >
                            <Delete16Regular />
                          </Button>
                        </ConfirmDeleteDialog>
                      </div>
                    </td>
                  </tr>
                );
              }

              return (
                <tr
                  key={row.id}
                  className={cn(
                    'group/row hover:bg-neutral-25 transition-colors',
                    isLastRow
                      ? ''
                      : isLastInGroup
                      ? 'border-b border-neutral-200'
                      : 'border-b border-neutral-100/60'
                  )}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const colId = cell.column.id;
                    const isAction = (cell.column.columnDef.meta as any)?.isAction;
                    const isLastCell = cellIndex === row.getVisibleCells().length - 1;

                    if ((colId === 'title' || colId === 'componentActions') && item.rowSpan === 0) {
                      return null;
                    }

                    const isGroupCell = colId === 'title' || colId === 'componentActions';

                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          'px-3 py-2 text-sm',
                          !isLastCell && 'border-r border-neutral-150',
                          isGroupCell && 'bg-neutral-25/50 align-top',
                          isAction && 'w-[44px] text-center'
                        )}
                        rowSpan={isGroupCell && item.rowSpan ? item.rowSpan : undefined}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-8 text-center text-neutral-400 text-sm"
              >
                Žádná data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default TypeMarkComponentTable;
